/**
 * Schermata "Oggi": la casa dell'app dopo il percorso di benvenuto.
 * - I promemoria di oggi: quelli arrivati si confermano con "Fatto" (o "Più tardi").
 *   Il nome del farmaco si vede qui, mai la dose (DEC-17).
 * - "Chiedi a mia …": la stessa chat del wizard.
 * - Notifiche sul telefono (DEC-04 rivista 2026-09-25) e numero sull'icona = promemoria da fare.
 */
import { useCallback, useEffect, useState } from 'react'

import type { ApiError } from '@mamma/contracts'

import { ApiFailure } from '../api/client'
import { oggiApi, type ReminderItem } from '../api/oggi'
import { wizardApi } from '../api/wizard'
import { applyAppName, askAgentLabel } from '../appName'
import {
  ErrorMessage,
  PrimaryButton,
  ProgressWords,
  QuestionTitle,
  SecondaryButton,
  WhyText,
  WizardFrame,
} from '../components'
import { aggiornaBadge, attiva, stato, type StatoNotifiche } from '../push'
import { AskAgentPanel } from '../wizard/AskAgentPanel'
import { PRIMA_AZIONE_KEY } from '../wizard/useWizard'

const ORA = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' })
const GIORNO = new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })

function saluto(now: Date): string {
  const h = now.getHours()
  return h < 13 ? 'Buongiorno' : h < 18 ? 'Buon pomeriggio' : 'Buonasera'
}

function oggi(now: Date): [Date, Date] {
  const inizio = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const fine = new Date(inizio.getTime() + 24 * 3600 * 1000 - 1)
  return [inizio, fine]
}

type Esito = { fase: 'caricamento' | 'pronto' | 'errore'; errore?: ApiError }

export function OggiRoute() {
  const [s, setS] = useState<Esito>({ fase: 'caricamento' })
  const [promemoria, setPromemoria] = useState<ReminderItem[]>([])
  const [forma, setForma] = useState<string | null>(null)
  const [benvenutoFinito, setBenvenutoFinito] = useState(true)
  const [notifiche, setNotifiche] = useState<StatoNotifiche | null>(null)
  // DEC-21: dopo il "Sì" a WQ-B13-1 il wizard lascia qui la prima domanda di mia; si legge
  // una volta sola (poi si toglie) e apre subito la chiacchierata.
  const [apertura] = useState<string | undefined>(() => {
    try {
      return window.sessionStorage.getItem(PRIMA_AZIONE_KEY) ?? undefined
    } catch {
      return undefined
    }
  })
  const [chiedi, setChiedi] = useState(() => Boolean(apertura))
  useEffect(() => {
    try {
      window.sessionStorage.removeItem(PRIMA_AZIONE_KEY)
    } catch {
      /* niente storage, niente da togliere */
    }
  }, [])
  const [busy, setBusy] = useState<string | null>(null)
  const [avviso, setAvviso] = useState<string | null>(null)
  const [now, setNow] = useState(() => new Date())

  const carica = useCallback(async () => {
    const adesso = new Date()
    setNow(adesso)
    try {
      const [inizio, fine] = oggi(adesso)
      const [lista, sessione] = await Promise.all([oggiApi.reminders(inizio, fine), wizardApi.session()])
      setPromemoria(lista.reminders)
      setForma(sessione.display_form)
      setBenvenutoFinito(sessione.session.status === 'completato')
      void applyAppName(sessione.display_form)
      aggiornaBadge(lista.reminders.filter((r) => new Date(r.at) <= adesso && !r.ack_status).length)
      setS({ fase: 'pronto' })
    } catch (problema) {
      const errore =
        problema instanceof ApiFailure
          ? problema.error
          : { code: 'imprevisto', message_it: 'Non riesco a caricare la giornata.', remedy_it: 'Tocchi Riprova.' }
      setS({ fase: 'errore', errore })
    }
  }, [])

  useEffect(() => {
    void carica()
    void stato().then(setNotifiche)
    const giro = window.setInterval(() => void carica(), 60_000)
    const torna = () => {
      if (document.visibilityState === 'visible') void carica()
    }
    document.addEventListener('visibilitychange', torna)
    return () => {
      window.clearInterval(giro)
      document.removeEventListener('visibilitychange', torna)
    }
  }, [carica])

  const conferma = async (id: string, esito: 'confermato' | 'rimandato') => {
    setBusy(id)
    try {
      await oggiApi.ack(id, esito)
      await carica()
    } catch (problema) {
      if (problema instanceof ApiFailure) setS({ fase: 'errore', errore: problema.error })
    } finally {
      setBusy(null)
    }
  }

  const attivaNotifiche = async () => {
    setBusy('notifiche')
    try {
      setNotifiche(await attiva())
    } catch {
      setAvviso('Non sono riuscita ad attivare le notifiche. Riprovi tra poco.')
    } finally {
      setBusy(null)
    }
  }

  const prova = async () => {
    setBusy('prova')
    try {
      const esito = await oggiApi.pushTest()
      setAvviso(esito.sent > 0 ? 'Le ho mandato una notifica di prova.' : 'Nessun telefono collegato: riattivi le notifiche.')
    } catch {
      setAvviso('La prova non è partita. Riprovi tra poco.')
    } finally {
      setBusy(null)
    }
  }

  const titolo = `${saluto(now)}${forma ? `, ${forma}` : ''}`
  const band = <ProgressWords block="Oggi" progress={GIORNO.format(now)} />

  if (s.fase === 'caricamento') {
    return (
      <WizardFrame band={band}>
        <QuestionTitle>{titolo}</QuestionTitle>
        <p className="text-body text-ink" role="status">
          Un momento…
        </p>
      </WizardFrame>
    )
  }

  if (s.fase === 'errore' && s.errore) {
    return (
      <WizardFrame band={band}>
        <QuestionTitle>{titolo}</QuestionTitle>
        <ErrorMessage error={s.errore} onRetry={() => void carica()} />
      </WizardFrame>
    )
  }

  const daFare = promemoria.filter((r) => new Date(r.at) <= now && !r.ack_status)
  const riassunto =
    promemoria.length === 0
      ? 'Per oggi non ci sono promemoria.'
      : daFare.length === 0
        ? 'Per adesso è tutto fatto.'
        : daFare.length === 1
          ? 'C’è una cosa da fare adesso.'
          : `Ci sono ${daFare.length} cose da fare adesso.`

  return (
    <WizardFrame band={band}>
      <QuestionTitle>{titolo}</QuestionTitle>
      <WhyText>{riassunto}</WhyText>

      {promemoria.length > 0 && (
        <ul className="mt-4 flex flex-col gap-3" aria-label="I promemoria di oggi">
          {promemoria.map((r) => (
            <RigaPromemoria key={r.id} r={r} now={now} busy={busy === r.id} onConferma={conferma} />
          ))}
        </ul>
      )}

      <div className="mt-auto">
        <PrimaryButton onClick={() => setChiedi(true)}>{askAgentLabel(forma)}</PrimaryButton>

        <div className="mt-3">
          {/* DEC-23: ricette, fogli e foto sue, fotografati e riletti da mia. */}
          <SecondaryButton onClick={() => window.location.assign('/documenti')}>Ricette, fogli e foto</SecondaryButton>
        </div>

        {!benvenutoFinito && (
          <div className="mt-3">
            <SecondaryButton onClick={() => window.location.assign('/wizard')}>Continuiamo a conoscerci</SecondaryButton>
          </div>
        )}

        <Notifiche stato={notifiche} busy={busy} onAttiva={attivaNotifiche} onProva={prova} />
        {avviso && (
          <p className="mt-3 text-body text-ink" role="status">
            {avviso}
          </p>
        )}
      </div>

      {chiedi && <AskAgentPanel label={askAgentLabel(forma)} closeLabel="Chiudi" opening={apertura} onClose={() => setChiedi(false)} />}
    </WizardFrame>
  )
}

function RigaPromemoria({
  r,
  now,
  busy,
  onConferma,
}: {
  r: ReminderItem
  now: Date
  busy: boolean
  onConferma: (id: string, esito: 'confermato' | 'rimandato') => void
}) {
  const quando = new Date(r.at)
  const arrivato = quando <= now
  const etichetta =
    r.ack_status === 'confermato'
      ? 'Fatto'
      : r.ack_status === 'rimandato'
        ? 'Rimandato'
        : arrivato
          ? 'Adesso'
          : 'Più tardi oggi'
  return (
    <li className="rounded-field border-2 border-line bg-surface px-4 py-3 text-ink">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-band font-bold">{ORA.format(quando)}</span>
        <span className="rounded-full bg-chip px-3 py-1 text-meta">{etichetta}</span>
      </div>
      <p className="mt-1 text-body">{r.title_it ?? 'Promemoria'}</p>
      {arrivato && !r.ack_status && (
        <div className="mt-2 flex flex-wrap gap-2.5">
          <SecondaryButton grow disabled={busy} onClick={() => onConferma(r.id, 'confermato')}>
            Fatto
          </SecondaryButton>
          <SecondaryButton grow disabled={busy} onClick={() => onConferma(r.id, 'rimandato')}>
            Più tardi
          </SecondaryButton>
        </div>
      )}
    </li>
  )
}

function Notifiche({
  stato: st,
  busy,
  onAttiva,
  onProva,
}: {
  stato: StatoNotifiche | null
  busy: string | null
  onAttiva: () => void
  onProva: () => void
}) {
  if (st === null || st === 'non_supportate') return null
  if (st === 'attive') {
    return (
      <div className="mt-3">
        <SecondaryButton disabled={busy === 'prova'} onClick={onProva}>
          Mandami una notifica di prova
        </SecondaryButton>
      </div>
    )
  }
  const testo =
    st === 'serve_home'
      ? 'Per ricevere i promemoria sul telefono, apra mia dall’icona sulla schermata Home.'
      : st === 'negate'
        ? 'Le notifiche di mia sono spente nelle impostazioni del telefono.'
        : 'Vuole che le ricordi le cose anche con l’app chiusa?'
  return (
    <div className="mt-4 rounded-field border-2 border-line bg-surface px-4 py-3">
      <p className="text-body text-ink">{testo}</p>
      {st === 'da_attivare' && (
        <div className="mt-2">
          <SecondaryButton disabled={busy === 'notifiche'} onClick={onAttiva}>
            Attiva le notifiche
          </SecondaryButton>
        </div>
      )}
    </div>
  )
}
